import Axios from 'axios';
import { agent } from '@/endpoints/agent';

let store = null;

function getSessionToken() {
  const { userDetails } = store?.state || {};
  return userDetails?.sessionId;
}

function createClient() {
  const token = getSessionToken();
  const headers = token ? { 'vektor-token': token } : {};
  const instance = Axios.create({
    responseType: 'json',
    timeout: 60000,
    headers,
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      console.error('[agentService]', error);
      return Promise.reject(error);
    },
  );

  return instance;
}

function buildWorkflowPayload(workflowView) {
  const { definition = {} } = workflowView;

  const nodes = (definition.nodes || [])
    .filter((n) => n?.id && (n?.type === 'UserInput' || n?.type === 'AgentAction'))
    .map((n) => {
      const d = n.data || {};
      const base = {
        id: n.id,
        type: d.type || n.type,
        name: d.name || n.label || n.type,
        actionType: d.actionType || n.type,
      };

      if (base.actionType === 'AgentAction') {
        return {
          ...base,
          agent: d.agent,
          maxTokenVisit: d.maxTokenVisit ?? null,
        };
      }

      return base;
    });

  const nodeIdSet = new Set(nodes.map((n) => n.id));

  const edges = (definition.edges || [])
    .filter((e) => e?.source && e?.target)
    .filter((e) => nodeIdSet.has(e.source) && nodeIdSet.has(e.target))
    .map((e) => ({
      source: e.source,
      target: e.target,
      condition: 'default',
    }));

  return {
    name: workflowView.name,
    description: workflowView.description || undefined,
    definition: { nodes, edges },
    ...(workflowView.version != null ? { version: workflowView.version } : {}),
  };
}

export default {
  init(vuexStore) {
    store = vuexStore;
  },

  async getWorkflows() {
    const client = createClient();
    const { data } = await client.get(agent.workflows);
    return data;
  },

  async getWorkflow(workflowId) {
    const client = createClient();
    const { data } = await client.get(agent.workflow(workflowId));
    return data;
  },

  buildSessionPayload(workflowView) {
    return buildWorkflowPayload(workflowView);
  },

  async startSession(payload) {
    const client = createClient();
    const { data } = await client.post(agent.sessions, payload);
    return data;
  },

  async getSession(sessionId) {
    const client = createClient();
    const { data } = await client.get(agent.session(sessionId));
    return data;
  },

  async getSessions() {
    const client = createClient();
    const { data } = await client.get(agent.sessions);
    return data;
  },

  async submitUserInput(sessionId, { tokenId, message }) {
    const client = createClient();
    const { data } = await client.post(agent.userInput(sessionId), { token_id: tokenId, message });
    return data;
  },

  async getTokenMessages(sessionId, tokenId) {
    const client = createClient();
    const { data } = await client.get(agent.tokenMessages(sessionId, tokenId));
    return data;
  },
};
